import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { Star } from "lucide-react";
import { getTeam } from "../lib/api";
import { GameCard } from "../components/GameCard";
import { useFavorite } from "../hooks/useFavorite";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const STADIUM = "https://images.pexels.com/photos/274577/pexels-photo-274577.jpeg";

const StatBox = ({ label, value, accent }) => (
  <div className="border border-border bg-card p-4">
    <p className="label-caps mb-1">{label}</p>
    <p className={`font-display text-4xl leading-none ${accent ? "text-primary" : ""}`}>{value}</p>
  </div>
);

export default function TeamDetail() {
  const { id } = useParams();
  const { fav, toggle } = useFavorite();
  const { data, isLoading } = useQuery({ queryKey: ["team", id], queryFn: () => getTeam(id) });

  if (isLoading) return <p className="text-muted-foreground">Loading team…</p>;
  if (!data) return <p className="text-muted-foreground">Team not found.</p>;

  const { team, games, roster, trend = [], gamesSeason, statsSeason } = data;
  const isFav = fav === team.id;

  return (
    <div data-testid="team-detail-page" className="space-y-8">
      <Link to="/standings" data-testid="team-back-link" className="label-caps hover:text-primary transition-colors duration-150">
        ← Back to Standings
      </Link>

      {/* Hero */}
      <section className="relative border border-border overflow-hidden">
        <img src={STADIUM} alt="stadium" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${team.color}cc, rgba(0,0,0,0.85))` }} />
        <div className="relative px-6 sm:px-10 py-12 flex items-center gap-6">
          <img src={team.logo} alt={team.name} className="w-24 h-24 sm:w-32 sm:h-32 object-contain" />
          <div>
            <p className="label-caps text-white/70">{team.division}</p>
            <h1 className="font-display text-6xl sm:text-7xl leading-none">{team.fullName}</h1>
            <p className="font-display text-4xl text-primary mt-1">
              {team.wins}-{team.losses}{team.ties ? `-${team.ties}` : ""}
              <span className="text-lg text-white/70 ml-3">{team.streak}</span>
            </p>
            <button
              data-testid="favorite-toggle"
              onClick={() => toggle(team.id)}
              className={`mt-3 flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border transition-colors duration-150 ${
                isFav
                  ? "bg-primary text-black border-primary"
                  : "border-white/40 text-white hover:border-primary hover:text-primary"
              }`}
            >
              <Star className={`w-4 h-4 ${isFav ? "fill-black" : ""}`} />
              {isFav ? "Your Team" : "Set as Favorite"}
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Win %" value={team.winPct.toFixed(3)} accent />
        <StatBox label="Points For" value={team.pointsFor} />
        <StatBox label="Points Against" value={team.pointsAgainst} />
        <StatBox label="Point Diff" value={(team.pointsFor - team.pointsAgainst > 0 ? "+" : "") + (team.pointsFor - team.pointsAgainst)} accent />
      </div>

      {trend.length > 0 && (
        <section data-testid="team-trend">
          <h2 className="font-display text-3xl mb-4 border-b-2 border-border pb-2" data-testid="team-trend-heading">{statsSeason} SEASON TREND</h2>
          <div className="flex flex-wrap gap-1.5 mb-5">
            {trend.map((t, i) => (
              <span
                key={i}
                title={`Wk ${t.week}: ${t.result} ${t.pf}-${t.pa} vs ${t.opponent}`}
                data-testid={`form-${i}`}
                className={`w-8 h-8 flex items-center justify-center font-display text-lg border ${
                  t.result === "W"
                    ? "bg-primary text-black border-primary"
                    : t.result === "L"
                    ? "bg-transparent text-accent border-accent/60"
                    : "bg-transparent text-muted-foreground border-border"
                }`}
              >
                {t.result}
              </span>
            ))}
          </div>
          <div className="border border-border bg-card p-5">
            <p className="label-caps mb-4">Cumulative Wins by Week</p>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={trend} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="winFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(75 100% 50%)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="hsl(75 100% 50%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="week" stroke="hsl(0 0% 45%)" tick={{ fontSize: 11 }} />
                <YAxis stroke="hsl(0 0% 45%)" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  cursor={{ stroke: "hsl(75 100% 50%)" }}
                  contentStyle={{ background: "hsl(0 0% 8%)", border: "1px solid hsl(75 100% 50%)" }}
                  labelStyle={{ color: "#fff" }}
                  formatter={(v) => [`${v} wins`, ""]}
                  labelFormatter={(w) => `Week ${w}`}
                />
                <Area type="monotone" dataKey="wins" stroke="hsl(75 100% 50%)" strokeWidth={2} fill="url(#winFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {roster.length > 0 && (
        <section>
          <h2 className="font-display text-3xl mb-4 border-b-2 border-border pb-2">KEY PLAYERS</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {roster.map((p) => (
              <div key={p.id} data-testid={`roster-player-${p.id}`} className="border border-border bg-card p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold">{p.name}</p>
                  <p className="label-caps">{p.position} · {p.category}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-3xl text-primary leading-none">{p.headline}</p>
                  <p className="text-xs text-muted-foreground">{p.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-display text-3xl mb-4 border-b-2 border-border pb-2" data-testid="team-games-heading">{gamesSeason} SEASON GAMES</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {games.map((g) => (
            <GameCard key={g.id} game={g} />
          ))}
        </div>
      </section>
    </div>
  );
}
